import { createContext, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg, fmtMoney, todayStr } from "@/lib/api";
import { ACTIVITY_TYPES, opts } from "@/lib/constants";
import { Field } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
export const useRecordTime = () => useContext(Ctx);

export function RecordTimeProvider({ children }) {
  const [state, setState] = useState({ open: false, matterId: null, onSaved: null });
  const openRecordTime = (matterId = null, onSaved = null) => setState({ open: true, matterId, onSaved });
  return (
    <Ctx.Provider value={{ openRecordTime }}>
      {children}
      <RecordTimeModal state={state} close={() => setState((s) => ({ ...s, open: false }))} />
    </Ctx.Provider>
  );
}

function RecordTimeModal({ state, close }) {
  const { user } = useAuth();
  const [matters, setMatters] = useState([]);
  const [users, setUsers] = useState([]);
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    setV({ matter_id: state.matterId || "", activity_type: "attendance", units: 1, description: "", date: todayStr(), user_id: user?.id });
    api.get("/matters", { params: { status: "Open" } }).then((r) => setMatters(r.data));
    if (user?.role === "admin") api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role !== "support")));
  }, [state.open]); // eslint-disable-line

  const feeEarner = users.find((u) => u.id === v.user_id) || user;
  const rate = feeEarner?.hourly_rate || 0;
  const hours = (Number(v.units) || 0) / 10;
  const set = (n, val) => setV((s) => ({ ...s, [n]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...v, units: Number(v.units) };
      if (user.role !== "admin") delete body.user_id;
      const r = await api.post("/time-entries", body);
      toast.success(`Recorded ${r.data.units} units (${r.data.hours}h) · ${fmtMoney(r.data.amount)}`);
      state.onSaved?.(r.data);
      close();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const fields = [
    { name: "matter_id", label: "Matter", type: "select", required: true, col: 2, options: matters.map((m) => ({ value: m.id, label: `${m.matter_number} · ${m.client_name} — ${m.description.slice(0, 50)}` })) },
    ...(user?.role === "admin" ? [{ name: "user_id", label: "Fee earner", type: "select", required: true, options: users.map((u) => ({ value: u.id, label: `${u.name} (${fmtMoney(u.hourly_rate)}/h)` })) }] : []),
    { name: "activity_type", label: "Activity type", type: "select", required: true, options: opts(ACTIVITY_TYPES) },
    { name: "units", label: "Duration (6-minute units)", type: "number", min: 1, required: true, hint: `${hours.toFixed(1)} hours · 1 unit = 6 minutes` },
    { name: "date", label: "Date", type: "date", required: true },
    { name: "description", label: "Description of work", type: "textarea", required: true, col: 2, placeholder: "e.g. Telephone attendance on client re settlement timetable" },
  ];

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel max-w-2xl" data-testid="record-time-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">Record Billable Time</DialogTitle>
          <DialogDescription className="text-slate-400">Time is recorded in 6-minute units (0.1 hour). An active cost agreement is required on the matter.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((f) => <Field key={f.name} f={f} value={v[f.name]} onChange={set} testPrefix="record-time" />)}
          <div className="md:col-span-2 panel bg-[#0B132B] p-4 flex items-center justify-between">
            <div className="text-xs text-slate-400">Rate <span className="font-mono gold-text">{fmtMoney(rate)}/h</span> × {hours.toFixed(1)}h</div>
            <div className="text-2xl font-serif text-white" data-testid="record-time-wip-total">{fmtMoney(hours * rate)} <span className="text-xs text-slate-400 font-sans">ex GST</span></div>
          </div>
          <div className="md:col-span-2 flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={close} data-testid="record-time-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="btn-gold" data-testid="record-time-modal-submit">{busy ? "Saving…" : "Save time entry"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
